if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true }),
        );
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(".product-form__quantity");
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this),
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach((element) =>
              element.classList.add("scroll-trigger--cancel"),
            ),
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;

        // Reset tracking for new variant change
        this._optimisticPriceUpdateSucceeded = false;

        this.resetProductFormState();

        // OPTIMISTIC UPDATE: Update UI immediately from client data
        const clientVariant =
          this.getVariantFromClientData(selectedOptionValues);
        if (clientVariant) {
          this.updateUIOptimistically(clientVariant);
        }

        // SERVER RECONCILIATION: Fetch HTML for full data
        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage,
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              console.log("[ProductInfo] Fetch aborted");
            } else {
              console.error(
                "[ProductInfo] Failed to fetch product info:",
                error,
              );
            }
            this.pendingRequestUrl = null;
            // Optimistic update already ran, so UI is functional
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "variant-selects [data-selected-variant]",
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      /**
       * Format money using shop's money format from Liquid.
       * Handles currency codes and international formats.
       */
      formatMoneyFromCents(cents) {
        if (cents == null) return "";

        const amount = cents / 100;

        // Use Intl.NumberFormat for locale-aware thousand separators and decimals
        // Thai locale uses comma for thousands, period for decimals: 1,690.00
        const formatter = new Intl.NumberFormat("th-TH", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

        const formattedAmount = formatter.format(amount);
        const formattedAmountNoDecimals = new Intl.NumberFormat("th-TH", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(Math.round(amount));

        const useCurrencyCode = window.currencyCodeEnabled || false;
        const format = useCurrencyCode
          ? window.shopMoneyFormatWithCurrency || "${{amount}} USD"
          : window.shopMoneyFormat || "${{amount}}";

        return format
          .replace("{{amount}}", formattedAmount)
          .replace("{{amount_no_decimals}}", formattedAmountNoDecimals);
      }

      /**
       * Find variant from client-side data based on selected options.
       * Returns null if data unavailable or parse fails.
       *
       * @param {Array<string>} selectedOptions - Array of option value IDs (e.g., ["7843269541176", "7843269607712"])
       * @returns {Object|null} - Matching variant object or null
       */
      getVariantFromClientData(selectedOptions) {
        // Check for parse failure from previous attempt
        if (this._variantDataParseFailed) {
          return null;
        }

        const variantDataEl = document.querySelector("[data-product-variants]");
        if (!variantDataEl) {
          console.warn("[ProductInfo] Variant data element not found");
          return null;
        }

        let variants;
        try {
          variants = JSON.parse(variantDataEl.textContent);
        } catch (e) {
          console.error(
            "[ProductInfo] Failed to parse variant data, falling back to server-only updates:",
            e,
          );
          this._variantDataParseFailed = true;
          return null;
        }

        // Validate structure
        if (!Array.isArray(variants) || variants.length === 0) {
          console.warn("[ProductInfo] Invalid variant data structure");
          this._variantDataParseFailed = true;
          return null;
        }

        // Convert option value IDs to text values for matching
        // selectedOptions contains IDs like ["7843269541176", "7843269607712"]
        // variant.options contains text like ["M", "Dark Grey"]
        const variantSelects = this.querySelector("variant-selects");
        if (!variantSelects) {
          console.warn(
            "[ProductInfo] VariantSelects element not found for option lookup",
          );
          return null;
        }

        const selectedTextValues = selectedOptions.map((optionValueId) => {
          // Find the option or input with this ID
          const element = variantSelects.querySelector(
            `option[data-option-value-id="${optionValueId}"], input[data-option-value-id="${optionValueId}"]`,
          );
          return element?.value || null;
        });

        // Validate all IDs were converted successfully
        if (selectedTextValues.some((val) => val === null)) {
          console.warn(
            "[ProductInfo] Could not convert all option IDs to text values",
            selectedTextValues,
          );
          return null;
        }

        // Find matching variant using text values
        const variant = variants.find((v) => {
          if (!v.options || v.options.length !== selectedTextValues.length) {
            return false;
          }
          return v.options.every((opt, idx) => opt === selectedTextValues[idx]);
        });

        return variant || null;
      }

      /**
       * Update price display using content-only updates.
       * Does NOT rebuild HTML - only updates text and toggles classes.
       * This prevents CSS duplication and maintains server-rendered structure.
       */
      updatePriceFromVariant(variant) {
        const priceEl = this.querySelector(`#price-${this.dataset.section}`);
        if (!priceEl || !variant) return;

        // Skip if product has volume pricing (price depends on quantity)
        if (this.dataset.volumePricing === "true") {
          return; // Let server handle volume pricing
        }

        const price = this.formatMoneyFromCents(variant.price);
        const compareAtPrice = variant.compare_at_price
          ? this.formatMoneyFromCents(variant.compare_at_price)
          : null;
        const onSale =
          compareAtPrice && variant.compare_at_price > variant.price;

        // Update price text (find existing elements, don't rebuild)
        const regularPriceEl = priceEl.querySelector(".price-item--regular");
        const salePriceEl = priceEl.querySelector(".price-item--sale");
        const compareEl = priceEl.querySelector("s.price-item--regular");

        if (regularPriceEl) {
          regularPriceEl.textContent = price;
        }

        if (salePriceEl && onSale) {
          salePriceEl.textContent = price;
        }

        if (compareEl && compareAtPrice && onSale) {
          compareEl.textContent = compareAtPrice;
        }

        // Toggle CSS classes (don't duplicate class logic)
        priceEl.classList.toggle("price--on-sale", onSale);
        priceEl.classList.toggle("price--sold-out", !variant.available);

        // Show/hide badge elements if they exist
        const saleBadge = priceEl.querySelector(".price__badge-sale");
        const soldOutBadge = priceEl.querySelector(".price__badge-sold-out");

        if (saleBadge) {
          saleBadge.style.display = onSale ? "" : "none";
        }

        if (soldOutBadge) {
          soldOutBadge.style.display = !variant.available ? "" : "none";
        }
      }

      /**
       * Optimistically update UI from client-side variant data.
       * Runs immediately before async server fetch for instant feedback.
       */
      updateUIOptimistically(variant) {
        if (!variant) {
          this.setUnavailable();
          this._optimisticPriceUpdateSucceeded = false;
          return;
        }

        // Update price display (skips if volume pricing)
        this.updatePriceFromVariant(variant);

        // Track success - skip server update for non-volume-pricing products
        this._optimisticPriceUpdateSucceeded =
          this.dataset.volumePricing !== "true";

        // Update button state
        this.productForm?.toggleSubmitButton(
          !variant.available,
          window.variantStrings.soldOut,
        );

        // Update variant inputs
        this.updateVariantInputs(variant.id);
      }

      buildRequestUrlWithParams(
        url,
        optionValueIds,
        shouldFetchFullPage = false,
      ) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        // Use option value IDs for server request
        if (optionValueIds.length) {
          params.push(`option_values=${optionValueIds.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html) {
        const oldVariantSelects = this.variantSelectors;
        const newVariantSelects = html.querySelector("variant-selects");

        if (!oldVariantSelects || !newVariantSelects) return;

        // Surgically update dropdown availability WITHOUT destroying components
        const oldDropdowns =
          oldVariantSelects.querySelectorAll("custom-dropdown");
        const newDropdowns =
          newVariantSelects.querySelectorAll("custom-dropdown");

        oldDropdowns.forEach((oldDropdown, index) => {
          const newDropdown = newDropdowns[index];
          if (!newDropdown) return;

          const oldMenu = oldDropdown.querySelector("[data-dropdown-menu]");
          const newMenu = newDropdown.querySelector("[data-dropdown-menu]");

          if (!oldMenu || !newMenu) return;

          // Copy availability and inventory from new to old
          const oldOptions = oldMenu.querySelectorAll('[role="option"]');
          const newOptions = newMenu.querySelectorAll('[role="option"]');

          oldOptions.forEach((oldOption, optIndex) => {
            const newOption = newOptions[optIndex];
            if (!newOption) return;

            // Update availability state
            const isDisabled = newOption.getAttribute("aria-disabled");
            if (isDisabled === "true") {
              oldOption.setAttribute("aria-disabled", "true");
            } else {
              oldOption.removeAttribute("aria-disabled");
            }

            // DON'T copy inventory display - let the custom-dropdown's PubSub
            // subscription handle updating inventory via updateInventoryDisplay()
            // The server HTML doesn't have JS-calculated inventory yet
          });
        });
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          // CRITICAL: Validate variant ID matches current selection
          const currentVariantInput = this.querySelector('input[name="id"]');
          const selectedVariantId = currentVariantInput?.value;
          if (
            selectedVariantId &&
            variant?.id?.toString() !== selectedVariantId
          ) {
            console.warn(
              `[ProductInfo] Variant mismatch - server returned ${variant?.id}, ` +
                `but current selection is ${selectedVariantId}. Skipping stale update.`,
            );
            return; // Don't apply stale server data
          }

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false,
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`,
            );
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };

          // Only update from server if element exists
          const serverPriceEl = html.getElementById(`price-${this.sectionId}`);
          if (serverPriceEl) {
            // Skip price update if optimistic update succeeded
            // Prevents unnecessary DOM replacement and flash
            // Keep server update for volume pricing (calculated server-side)
            if (!this._optimisticPriceUpdateSucceeded) {
              updateSourceFromDestination("price");
            }
          } else {
            console.warn(
              `[ProductInfo] Server response missing price element. Using optimistic update.`,
            );
          }
          updateSourceFromDestination("Sku", ({ classList }) =>
            classList.contains("hidden"),
          );
          updateSourceFromDestination(
            "Inventory",
            ({ innerText }) => innerText === "",
          );
          updateSourceFromDestination("Volume");
          updateSourceFromDestination("Price-Per-Item", ({ classList }) =>
            classList.contains("hidden"),
          );

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`,
          )?.classList.remove("hidden");
          this.querySelector(
            `#Volume-Note-${this.dataset.section}`,
          )?.classList.remove("hidden");

          // Only update button from server if element exists
          const submitButton = html.getElementById(
            `ProductSubmitButton-${this.sectionId}`,
          );
          if (submitButton) {
            this.productForm?.toggleSubmitButton(
              submitButton.hasAttribute("disabled"),
              window.variantStrings.soldOut,
            );
          }
          // Optimistic update already set correct state if element missing

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ""}`,
        );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`,
        );
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable,
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Price-Per-Item",
          "Volume-Note",
          "Volume",
          "Quantity-Rules",
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector("media-gallery ul");
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute("data-zoom-on-hover")) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(
            mediaGallerySource.querySelectorAll("li[data-media-id]"),
          );
          const sourceSet = new Set(
            mediaGallerySourceItems.map((item) => item.dataset.mediaId),
          );
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [
              item.dataset.mediaId,
              { item, index },
            ]),
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] =
            refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll("li[data-media-id]"),
          );
          const destinationSet = new Set(
            mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId),
          );
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (
              !sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)
            ) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (
              !destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)
            ) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh)
            [mediaGallerySourceItems, sourceSet, sourceMap] =
              refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach(
            (destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

              if (sourceData && sourceData.index !== destinationIndex) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(
                    `li:nth-of-type(${destinationIndex + 1})`,
                  ),
                );

                // refresh source now that it has been modified
                [mediaGallerySourceItems, sourceSet, sourceMap] =
                  refreshSourceData();
              }
            },
          );
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(
          `.product-media-modal__content`,
        );
        const newModalContent = html.querySelector(
          `product-modal .product-media-modal__content`,
        );
        if (modalContent && newModalContent)
          modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner",
        ).classList.remove("hidden");
        return fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`,
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner",
            ).classList.add("hidden"),
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`,
        );
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label",
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step",
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products",
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`,
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list",
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`,
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    },
  );
}
